/**
 * SlashCommands - Tiptap extension for "/" command palette
 *
 * Triggers when user types "/" and shows available block types.
 * Uses @tiptap/suggestion for autocomplete behavior.
 *
 * Available commands:
 * - Headings (H1, H2, H3)
 * - Lists (bullet, ordered)
 * - Code block
 * - Table (3x3)
 * - Blockquote
 * - Horizontal rule
 * - Ask AI (opens AIPromptModal for content generation)
 * - Continue writing (AI continues from cursor with context)
 *
 * How it works:
 * 1. User types "/" - suggestion plugin detects the trigger character
 * 2. SlashMenu component renders via ReactRenderer
 * 3. tippy.js positions the menu near the cursor
 * 4. User navigates with keyboard or mouse
 * 5. Selection executes command and removes "/" from document
 */

import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance } from 'tippy.js';
import { SlashMenu, type SlashMenuItem, type SlashMenuRef } from '../SlashMenu';

/**
 * SLASH_COMMANDS - All available commands in the palette
 *
 * Each command has:
 * - title: Display name for the menu
 * - description: Brief explanation
 * - icon: Visual indicator (text-based)
 * - command: Function to execute (receives editor and range)
 *
 * The command function typically:
 * 1. Focuses the editor
 * 2. Deletes the "/" trigger range
 * 3. Applies the desired formatting/insertion
 */
const SLASH_COMMANDS: SlashMenuItem[] = [
  // ========== HEADINGS ==========
  {
    title: 'Heading 1',
    description: 'Large section heading',
    icon: 'H1',
    command: ({ editor, range }) => {
      // Chain: focus -> delete "/" -> set node to heading level 1
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 1 })
        .run();
    },
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    icon: 'H2',
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 2 })
        .run();
    },
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    icon: 'H3',
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 3 })
        .run();
    },
  },

  // ========== LISTS ==========
  {
    title: 'Bullet List',
    description: 'Create a bulleted list',
    icon: '\u2022', // Unicode bullet character
    command: ({ editor, range }) => {
      // toggleBulletList creates or toggles bullet list formatting
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: 'Numbered List',
    description: 'Create a numbered list',
    icon: '1.',
    command: ({ editor, range }) => {
      // toggleOrderedList creates or toggles numbered list formatting
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },

  // ========== CODE ==========
  {
    title: 'Code Block',
    description: 'Add a code block with syntax highlighting',
    icon: '</>',
    command: ({ editor, range }) => {
      // toggleCodeBlock creates a code block (uses CodeBlockLowlight if installed)
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },

  // ========== TABLE ==========
  {
    title: 'Table',
    description: 'Insert a 3x3 table',
    icon: '\u25A6', // Unicode square with lines
    command: ({ editor, range }) => {
      // insertTable creates a table with specified dimensions
      // withHeaderRow: true makes the first row a header row
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },

  // ========== OTHER BLOCKS ==========
  {
    title: 'Quote',
    description: 'Add a blockquote',
    icon: '"',
    command: ({ editor, range }) => {
      // toggleBlockquote creates indented quote block
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: 'Divider',
    description: 'Insert a horizontal line',
    icon: '\u2014', // Unicode em dash
    command: ({ editor, range }) => {
      // setHorizontalRule inserts <hr> element
      editor.chain().focus().deleteRange(range).setHorizontalRule().run();
    },
  },

  // ========== AI COMMANDS ==========
  // These commands trigger the AIPromptModal for AI-generated content
  {
    title: 'Ask AI',
    description: 'Generate content with AI',
    icon: '\u2728', // Sparkle emoji - matches AI theme
    command: ({ editor, range }) => {
      // Delete the "/" trigger first
      editor.chain().focus().deleteRange(range).run();

      // Store the request in editor storage for the modal to pick up
      // The modal will be rendered by RichTextEditor
      const storage = editor.storage as {
        aiPrompt?: {
          isOpen: boolean;
          mode: 'generate' | 'continue';
          cursorPosition: number;
          context?: string;
        };
      };

      // Initialize storage if needed
      if (!storage.aiPrompt) {
        storage.aiPrompt = { isOpen: false, mode: 'generate', cursorPosition: 0 };
      }

      // Set state for modal to show
      storage.aiPrompt.isOpen = true;
      storage.aiPrompt.mode = 'generate';
      storage.aiPrompt.cursorPosition = editor.state.selection.from;

      // Emit custom event for React to handle
      // RichTextEditor listens for this event and shows AIPromptModal
      editor.emit('aiPromptOpen', { mode: 'generate' });
    },
  },
  {
    title: 'Continue writing',
    description: 'AI continues from cursor position',
    icon: '\u270D\uFE0F', // Writing hand emoji
    command: ({ editor, range }) => {
      // Delete the "/" trigger first
      editor.chain().focus().deleteRange(range).run();

      // Get context - last 500 chars before cursor for AI to understand
      const pos = editor.state.selection.from;
      const context = editor.state.doc.textBetween(Math.max(0, pos - 500), pos);

      // Store the request in editor storage
      const storage = editor.storage as {
        aiPrompt?: {
          isOpen: boolean;
          mode: 'generate' | 'continue';
          cursorPosition: number;
          context?: string;
        };
      };

      // Initialize storage if needed
      if (!storage.aiPrompt) {
        storage.aiPrompt = { isOpen: false, mode: 'generate', cursorPosition: 0 };
      }

      // Set state for modal - includes context for continue mode
      storage.aiPrompt.isOpen = true;
      storage.aiPrompt.mode = 'continue';
      storage.aiPrompt.cursorPosition = pos;
      storage.aiPrompt.context = context;

      // Emit custom event for React to handle
      // RichTextEditor listens for this event and shows AIPromptModal with context
      editor.emit('aiPromptOpen', { mode: 'continue', context });
    },
  },
];

/**
 * SlashCommands Extension
 *
 * Creates a Tiptap extension that:
 * 1. Listens for "/" character input
 * 2. Shows filtered command list
 * 3. Executes selected command
 */
export const SlashCommands = Extension.create({
  name: 'slashCommands',

  /**
   * addOptions - Configure the suggestion plugin
   *
   * - char: The trigger character ("/")
   * - startOfLine: false means "/" works anywhere, not just at line start
   * - command: Called when user selects an item
   */
  addOptions() {
    return {
      suggestion: {
        char: '/',
        startOfLine: false, // Allow "/" anywhere, not just at line start
        command: ({
          editor,
          range,
          props,
        }: {
          editor: any;
          range: any;
          props: SlashMenuItem;
        }) => {
          // props is the selected SlashMenuItem
          // Call its command function with editor and range
          props.command({ editor, range });
        },
      },
    };
  },

  /**
   * addProseMirrorPlugins - Register the Suggestion plugin
   *
   * The Suggestion plugin handles:
   * - Detecting trigger character
   * - Filtering items based on query
   * - Rendering popup via render()
   * - Keyboard navigation
   */
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,

        /**
         * items - Filter commands based on user query
         *
         * @param query - Text typed after "/" (e.g., "head" for "/head")
         * @returns Filtered list of matching commands
         */
        items: ({ query }: { query: string }) => {
          // Case-insensitive search in title and description
          const search = query.toLowerCase();
          return SLASH_COMMANDS.filter(
            (item) =>
              item.title.toLowerCase().includes(search) ||
              item.description.toLowerCase().includes(search)
          );
        },

        /**
         * render - Handle popup lifecycle
         *
         * Returns an object with callbacks for:
         * - onStart: When "/" is typed, show the menu
         * - onUpdate: As user types more, update position/items
         * - onKeyDown: Handle keyboard navigation
         * - onExit: When menu closes, cleanup
         */
        render: () => {
          // These hold references to the React component and tippy popup
          let component: ReactRenderer<SlashMenuRef>;
          let popup: Instance[];

          return {
            /**
             * onStart - Called when "/" is typed
             *
             * Creates the React component and tippy popup
             */
            onStart: (props: any) => {
              // Create React component with initial props
              component = new ReactRenderer(SlashMenu, {
                props,
                editor: props.editor,
              });

              // Don't show if we don't have position info
              if (!props.clientRect) {
                return;
              }

              // Create tippy popup positioned near the cursor
              popup = tippy('body', {
                // Get position from ProseMirror (follows cursor)
                getReferenceClientRect: props.clientRect,
                // Append to body for proper z-index
                appendTo: () => document.body,
                // Our React component is the content
                content: component.element,
                // Show immediately (we control visibility)
                showOnCreate: true,
                // Allow clicks inside the popup
                interactive: true,
                // We control when to show/hide
                trigger: 'manual',
                // Position below and to the left of cursor
                placement: 'bottom-start',
              });
            },

            /**
             * onUpdate - Called as user types after "/"
             *
             * Updates props (filtered items) and position
             */
            onUpdate: (props: any) => {
              // Update React component with new filtered items
              component.updateProps(props);

              if (!props.clientRect) {
                return;
              }

              // Update position (cursor may have moved)
              popup[0]?.setProps({
                getReferenceClientRect: props.clientRect,
              });
            },

            /**
             * onKeyDown - Handle keyboard events
             *
             * Escape closes the menu, other keys go to SlashMenu component
             */
            onKeyDown: (props: any) => {
              // Escape closes the menu
              if (props.event.key === 'Escape') {
                popup[0]?.hide();
                return true;
              }

              // Pass to SlashMenu component for arrow/enter handling
              return component.ref?.onKeyDown(props) ?? false;
            },

            /**
             * onExit - Called when menu should close
             *
             * Cleanup tippy and React component
             */
            onExit: () => {
              popup[0]?.destroy();
              component.destroy();
            },
          };
        },
      }),
    ];
  },
});
